import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Eye, RotateCcw, CheckCircle2, Loader2 } from 'lucide-react';
import { usePromptVersions, usePromptVersionById, useRestorePromptVersion } from '@/hooks/usePromptVersions';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { PromptVersionListItem } from '@/types/api/prompt-versions';

interface PromptHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PromptHistorySheet({ open, onOpenChange }: PromptHistorySheetProps) {
  const { toast } = useToast();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [restoreVersionId, setRestoreVersionId] = useState<string | null>(null);

  const { data: versionsData, isLoading } = usePromptVersions(1, 50);
  const { data: selectedVersion, isLoading: isLoadingVersion } = usePromptVersionById(selectedVersionId);
  const restoreMutation = useRestorePromptVersion();

  const handleRestore = async () => {
    if (!restoreVersionId) return;

    try {
      await restoreMutation.mutateAsync(restoreVersionId);
      toast({
        title: 'Sucesso',
        description: 'Conteúdo restaurado para o rascunho. Revise e publique.',
      });
      setRestoreVersionId(null);
      setSelectedVersionId(null);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível restaurar a versão',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateStr: string) => {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: ptBR });
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader>
            <SheetTitle>Histórico de Versões</SheetTitle>
            <SheetDescription>
              Visualize e restaure versões anteriores do prompt
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 py-4 overflow-y-auto">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="p-3 border rounded-lg space-y-2">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : versionsData?.items.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Nenhuma versão publicada ainda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {versionsData?.items.map((version) => (
                  <VersionItem
                    key={version.id}
                    version={version}
                    isSelected={selectedVersionId === version.id}
                    onView={() => setSelectedVersionId(version.id)}
                    onRestore={() => setRestoreVersionId(version.id)}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            )}

            {/* Version detail view */}
            {selectedVersionId && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium mb-3">Detalhes da Versão</h4>
                {isLoadingVersion ? (
                  <div className="space-y-2">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                  </div>
                ) : selectedVersion ? (
                  <div className="space-y-4 text-sm">
                    {selectedVersion.identity && (
                      <div>
                        <p className="font-medium text-muted-foreground">Identidade</p>
                        <p className="whitespace-pre-wrap">{selectedVersion.identity}</p>
                      </div>
                    )}
                    {selectedVersion.mission && (
                      <div>
                        <p className="font-medium text-muted-foreground">Missão</p>
                        <p className="whitespace-pre-wrap">{selectedVersion.mission}</p>
                      </div>
                    )}
                    {selectedVersion.tone && (
                      <div>
                        <p className="font-medium text-muted-foreground">Tom</p>
                        <p className="whitespace-pre-wrap">{selectedVersion.tone}</p>
                      </div>
                    )}
                    {selectedVersion.capabilities && (
                      <div>
                        <p className="font-medium text-muted-foreground">Capacidades</p>
                        <p className="whitespace-pre-wrap">{selectedVersion.capabilities}</p>
                      </div>
                    )}
                    {selectedVersion.responseRules && (
                      <div>
                        <p className="font-medium text-muted-foreground">Regras de Resposta</p>
                        <p className="whitespace-pre-wrap">{selectedVersion.responseRules}</p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Restore confirmation dialog */}
      <AlertDialog open={!!restoreVersionId} onOpenChange={(open) => !open && setRestoreVersionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar versão?</AlertDialogTitle>
            <AlertDialogDescription>
              O conteúdo desta versão será copiado para o rascunho atual.
              Você poderá revisar e fazer ajustes antes de publicar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Restaurar para Rascunho
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface VersionItemProps {
  version: PromptVersionListItem;
  isSelected: boolean;
  onView: () => void;
  onRestore: () => void;
  formatDate: (date: string) => string;
}

function VersionItem({ version, isSelected, onView, onRestore, formatDate }: VersionItemProps) {
  return (
    <div
      className={`p-3 border rounded-lg transition-colors ${
        isSelected ? 'border-primary bg-primary/5' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">Versão {version.versionNumber}.0</span>
            {version.isActive && (
              <Badge variant="default" className="text-xs">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Ativa
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {formatDate(version.publishedAt)} por {version.publisherName}
          </p>
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onView}
            title="Ver detalhes"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {!version.isActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRestore}
              title="Restaurar para rascunho"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
